import {autoSpaces, getObjectKey} from '@services/helpers.ts'

export interface IOrderEntry {
  id: string,
  orderType: OrderType,
  orderSide: OrderSide,
  limitOptions: LimitOptions,
  cancelReason : OrderCancelReasonDto,
  symbol: string,
  price: string,
  volume: number,
  cost: number,
  fee: number,
  state: OrderDisplayStatus,
  status: OrderStatus,
  createdDate: string,
  closedDate: string,
  account: string,
  avgPrice: number,
}

export interface IOrderFilters {
  id: string | null,
  account: string | null,
  createdDate: {
    from: string | null,
    to: string | null
  } | null,
  closedDate: {
    from: string | null,
    to: string | null
  } | null,
  orderType: OrderType | null,
  orderSide: OrderSide | null,
  orderArea: OrderTradingArea | null,
  orderOwner: OrderOwners | null,
  status: OrderStatus | null,
  displayStatuses: OrderDisplayStatus | null,
  displayStatus: OrderDisplayStatus | null,
  limitOptions: LimitOptions | null,
  symbol: string| null,
  ownerRole: OrderTradingRole | null,
  externalOperationId: string | null
}

export enum LimitOptions {
  GoodTillCanceled = 'GoodTillCanceled',
  ImmediateOrCancel = 'ImmediateOrCancel',
  GoodTillTime = 'GoodTillTime'
}


export enum OrderDisplayStatus {
  Open = 'Open',
  Canceled = 'Canceled',
  Filled = 'Filled',
  PartiallyFilled = 'PartiallyFilled'
}

export enum OrderStatus {
  Checked = 'Checked', // в assetManagement
  Placed = 'Placed', // выставлен в матчере
  Filled = 'Filled', // весь выкуплен
  Canceled = 'Canceled', // отменен после выставления
  Rejected = 'Rejected', // отклонен при выставлении (не был в матчере)
  Expired = 'Expired', // истек GTT
  PartiallyFilled = 'PartiallyFilled', // частично куплен (промежуточный)
  Activated = 'Activated', // активирован STOP
}

export enum OrderStatusView {
  Placed = OrderStatus.Placed,
  Filled = OrderStatus.Filled,
  Canceled = OrderStatus.Canceled,
  Rejected = OrderStatus.Rejected,
  PartiallyFilled = OrderStatus.PartiallyFilled,
}

export enum OrderSide {
  Buy = 'Buy',
  Sell = 'Sell'
}

export enum OrderType {
  Limit = 'Limit',
  Market = 'Market'
}

export enum OrderTradingArea {
  Spot = 'Spot'
}

export enum OrderTradingRole {
  Regular =  'Regular',
  Broker = 'Broker'
}

export function useFilterOrderTradingRole (value: OrderTradingRole) {
  const map = {
    [OrderTradingRole.Regular]: 'Розничный клиент',
    [OrderTradingRole.Broker]: 'Брокер',
  }

  return map[value] || autoSpaces(getObjectKey(value, OrderTradingRole))
}

export function useFilterOrderType(value: OrderType) {
  const map = {
    [OrderType.Limit]: 'По фикс. цене',
    [OrderType.Market]: 'По рынку'
  }

  return map[value] || autoSpaces(getObjectKey(value, OrderType))
}


export function useFilterOrderDisplayStatus(value: OrderDisplayStatus) {
  const map = {
    [OrderDisplayStatus.Filled]: 'Заполнен',
    [OrderDisplayStatus.Canceled]: 'Отменен',
    [OrderDisplayStatus.PartiallyFilled]: 'Частично заполнен',
    [OrderDisplayStatus.Open]: 'Открыт',
  }

  return map[value] || autoSpaces(getObjectKey(value, OrderDisplayStatus))
}

export function useFilterOrderStatus(value: OrderStatus) {
  const map = {
    [OrderStatus.Checked]: 'Проверен',
    [OrderStatus.Placed]: 'Создан',
    [OrderStatus.Filled]: 'Заполнен',
    [OrderStatus.Canceled]: 'Отменен',
    [OrderStatus.Rejected]: 'Отклонен',
    [OrderStatus.PartiallyFilled]: 'Частично заполнен',
    [OrderStatus.Activated]: 'Активный',
    [OrderStatus.Expired]: 'Истек'
  }

  return map[value] || autoSpaces(getObjectKey(value, OrderStatus))
}

export function useFilterOrderTradingArea(value: OrderTradingArea) {
  const map = {
    [OrderTradingArea.Spot]: 'Cпот'
  }

  return map[value] || autoSpaces(getObjectKey(value, OrderTradingArea))
}

export function useFilterOrderSide(value: OrderSide) {
  const map = {
    [OrderSide.Buy]: 'Покупка',
    [OrderSide.Sell]: 'Продажа',
  }

  return map[value] || autoSpaces(getObjectKey(value, OrderSide))
}

export enum RuTransportOperationType {
  Invoice =  'Invoice',
  Payout = 'Payout',
  Withdrawal = 'Withdrawal',
  Deposit = 'Deposit',
  AppealPayout = 'Appeal',
  ManualCorrectionEnrollment = 'Correction',
}

export enum OrderCancelReasonDto {
  CanceledByTrader = 'CanceledByTrader',
  NotEnoughMoney = 'NotEnoughMoney',
  SlippageExceeded = 'SlippageExceeded',
  CanceledByBroker = 'CanceledByBroker',
}

export enum OrderOwners {
  Broker = 'Broker',
  Liquidity = 'Liquidity',
}

export enum OrderColumns {
  id = 'id',
  type = 'type',
  options = 'options',
  symbol = 'symbol',
  price = 'price',
  volume = 'volume',
  executedFee = 'executedFee',
  cost = 'cost',
  state = 'state',
  status = 'status',
  creationTime = 'creationTime',
  closingTime = 'closingTime',
  account = 'account',
  ownerRole = 'ownerRole'
}

export function useFilterOrderTypeSide(type: OrderType, side: OrderSide) {
    switch (type) {
      case OrderType.Limit:
      {
        switch (side) {
          case OrderSide.Buy:
            return 'components.order.limitBuy';
          case OrderSide.Sell:
            return 'components.order.limitSell';
          default: return '';
        }
      }
      case OrderType.Market:{
        switch (side) {
          case OrderSide.Buy:
            return 'components.order.marketBuy';
          case OrderSide.Sell:
            return 'components.order.marketSell';
          default: return '';
        }
      }
      default: return '';
    }
}
